import {
  ArrowLeftIcon,
  ArrowRightIcon,
  DownloadIcon,
} from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Button,
  Card,
  ChevronDownIcon,
  Flex,
  Heading,
  IconButton,
  SegmentedControl,
  Skeleton,
  Text,
} from '@radix-ui/themes';

import { FileHelper } from 'classes/helpers/file.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { BlurOverlay } from 'components/common/blur-overlay';
import { CommonBackArrow } from 'components/common/buttons/back-arrow';
import { DetectionFlag } from 'components/common/custom-icon/shorthands';
import { DialogButton } from 'components/common/dialogs/button';
import FloatingCopyButton from 'components/common/form/floating-copy-button';
import { JsonViewer } from 'components/common/json/json-viewer';
import { CommonMenu } from 'components/common/menu';
import { CommonTableHoC } from 'components/common/table';
import { CommonTabs } from 'components/common/tabs';
import { CommonTooltip } from 'components/common/tooltip';
import {
  AnnotatedVideoPlayer,
  IAnnotationDetails,
} from 'components/common/video/annotated-player';
import { formatDate } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/common';
import { MachineShotConfidenceBadge } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/machine-shot-confidence-badge';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { DetectionIssueDialog } from 'components/sections/analytics/session-hits/detection-issue-dialog';
import { IZipFile } from 'interfaces/forms/file';
import { IMenuAction } from 'interfaces/i-menus';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { HitHelper } from 'lib_ts/classes/hit.helper';
import { METERS_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { S3Command } from 'lib_ts/enums/data.enums';
import { OutcomeCode } from 'lib_ts/enums/hitters.enums';
import { VISION_ENABLED_HW_VERSIONS } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { PrecursorTrackingDeviceMap } from 'lib_ts/enums/training.enums';
import { StereoVideoType } from 'lib_ts/enums/videos.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IStereoVideo } from 'lib_ts/interfaces/i-stereo-video';
import { IPitch, IPlateLoc, ITrajektRefBreak } from 'lib_ts/interfaces/pitches';
import { ISHHitDetails } from 'lib_ts/interfaces/session-hits/i-hit-details';
import {
  IDetectionIssue,
  ISessionHit,
} from 'lib_ts/interfaces/session-hits/i-session-hit';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import {
  IShotPrecursor,
  IShotPrecursorSummary,
} from 'lib_ts/interfaces/training/i-shot-precursor';
import { compact, first, isNil, uniq } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HittersService } from 'services/hitters.service';
import { ShotsService } from 'services/shots.service';
import { StereoVideosService } from 'services/stereo-videos.service';
import useSignedUrlsStore from 'stores/use-signed-urls-store';

interface IShotDetailsProps {
  shot: IMachineShot;
  pitch: IPitch;
  hasPrev: boolean;
  hasNext: boolean;
  loadingPrev: boolean;
  onBack: () => void;
  onPrevShot: () => void;
  onNextShot: () => void;
}

type IShotDataRow<T> = T & {
  _id: string;
  label: string;
  subLabel?: string;
};

interface IShotPitchChars {
  speedMPH?: number;
  netSpin?: number;
  plate?: IPlateLoc;
  break?: ITrajektRefBreak;
}

type IPitchDataRow = IShotDataRow<IShotPitchChars>;
type IHitDataRow = IShotDataRow<Partial<ISHHitDetails>>;

enum ShotDetailsTabKey {
  PitchData = 'Pitch Data',
  HitData = 'Hit Data',
  MachineShot = 'Machine Shot',
  RapsodoShot = 'Rapsodo',
  TrackmanShot = 'Trackman',
  MsbsShot = 'MSBS',
  HawkeyeShot = 'Hawkeye',
}

const getShotTarget = (shot: IMachineShot): IShotPitchChars => {
  let breaks: ITrajektRefBreak | undefined;
  let speedMPH: number | undefined;
  let netSpin: number | undefined;
  let plate: IPlateLoc | undefined;

  if (shot.target_traj) {
    speedMPH = TrajHelper.getSpeedFromTraj(shot.target_traj);
    netSpin = shot.target_bs
      ? BallHelper.getNetSpin(shot.target_bs)
      : undefined;
    breaks = TrajHelper.getBreaks(shot.target_traj);
    plate = TrajHelper.getPlateLoc(shot.target_traj);
  }

  return {
    speedMPH,
    netSpin,
    plate,
    break: breaks,
  };
};

const getShotResult = (shot: IMachineShot): IShotPitchChars => {
  const output: IShotPitchChars = {
    speedMPH: shot.traj
      ? TrajHelper.getSpeedFromTraj(shot.traj)
      : shot.bs
        ? BallHelper.getSpeedFromBs(shot.bs)
        : undefined,

    break: TrajHelper.getBreaksFromShot(shot),
    plate: shot.traj ? TrajHelper.getPlateLoc(shot.traj) : undefined,

    netSpin: shot.bs ? BallHelper.getNetSpin(shot.bs) : undefined,
  };

  return output;
};

const getPitchDataRows = (
  shot: IMachineShot,
  hasVision: boolean,
  precursors: IShotPrecursor[]
): IPitchDataRow[] => {
  const target = getShotTarget(shot);

  const rows: IPitchDataRow[] = [
    {
      _id: 'Target',
      label: 'Target',
      ...target,
    },
  ];

  const shotResult = getShotResult(shot);

  rows.push({
    _id: 'Tracking Device',
    label: 'Tracking Device',
    subLabel: shot.tracking_device,
    ...shotResult,
  });

  precursors.forEach((precursor) => {
    if (precursor.source === 'vision' && !hasVision) {
      return;
    }

    const breaks = precursor.break
      ? {
          xInches: -1 * precursor.break.PITCH_HBTrajectory * METERS_TO_INCHES,
          zInches: precursor.break.PITCH_VBTrajectory * METERS_TO_INCHES,
        }
      : TrajHelper.getBreaks(precursor.traj);

    rows.push({
      _id: precursor.source,
      label: PrecursorTrackingDeviceMap[precursor.source],

      speedMPH: precursor.traj
        ? TrajHelper.getSpeedFromTraj(precursor.traj)
        : precursor.bs
          ? BallHelper.getSpeedFromBs(precursor.bs)
          : undefined,

      break: breaks,
      plate: precursor.traj
        ? TrajHelper.getPlateLoc(precursor.traj)
        : undefined,

      netSpin: precursor.bs ? BallHelper.getNetSpin(precursor.bs) : undefined,
    });
  });

  return rows;
};

const getHitDataRows = (
  shot: IMachineShot,
  hasVision: boolean,
  precursors: IShotPrecursor[],
  sessionHit?: ISessionHit
): IHitDataRow[] => {
  const rows: IHitDataRow[] = [
    {
      _id: 'Tracking Device',
      label: 'Tracking Device',
      subLabel: shot.tracking_device,
      ...sessionHit?.hit,
    },
  ];

  precursors.forEach((precursor) => {
    if (precursor.source === 'vision' && !hasVision) {
      return;
    }

    if (!precursor.hit?.hit?._mx_outcome) {
      return;
    }

    rows.push({
      _id: precursor.source,
      label: PrecursorTrackingDeviceMap[precursor.source],
      ...precursor.hit?.hit,
    });
  });

  return rows;
};

const getDefaultVideoType = (shot: IMachineShot) => {
  if (shot.annotated_video_id) {
    return StereoVideoType.Annotated;
  }

  if (shot.replay_video_id) {
    return StereoVideoType.Replay;
  }

  if (shot.raw_video_id) {
    return StereoVideoType.Raw;
  }

  if (shot.spins_video_id) {
    return StereoVideoType.Spins;
  }
};

const DownloadVideosButton = (props: {
  shotID: string;
  annotatedVideo?: IStereoVideo;
  replayVideo?: IStereoVideo;
  rawVideo?: IStereoVideo;
  spinsVideo?: IStereoVideo;
  ready: boolean;
}) => {
  const [loading, setLoading] = useState(false);

  const onClick = useCallback(
    async (videos: { name: string; video: IStereoVideo }[]) => {
      setLoading(true);

      try {
        const validVideos = videos.filter((m) => !!m.video.path);

        const signed: IZipFile[] = await Promise.all(
          validVideos.map(async (m) => ({
            name: m.name,
            url: await useSignedUrlsStore
              .getState()
              .getSignedUrl(
                m.video.region,
                m.video.path,
                S3Command.getObject,
                undefined,
                true
              ),
          }))
        );

        // just in case we fail to sign something
        const signedVideos = signed.filter((video) => !!video.url);

        if (signedVideos.length === 0) {
          return;
        }

        // no need to zip if we're only downloading one video
        if (signedVideos.length === 1) {
          window.open(signedVideos[0]?.url, '_blank');
          return;
        }

        await FileHelper.zipFiles({
          fileName: `${props.shotID} Videos`,
          files: signedVideos.map((video) => {
            // FW wants 'spin' instead of 'spins' in the filename
            const typeSuffix =
              video.name === StereoVideoType.Spins ? 'spin' : video.name;

            const name = `${props.shotID}-${typeSuffix}`;
            return {
              name,
              url: video.url as string,
            };
          }),
        });
      } catch (error) {
        console.error('Error downloading files:', error);
        NotifyHelper.error({
          message_md:
            'We were unable to download the videos. Please try again later.',
        });
      } finally {
        setLoading(false);
      }
    },
    [props.shotID]
  );

  const actions = useMemo<IMenuAction[]>(() => {
    const acts: IMenuAction[] = [];

    if (props.annotatedVideo) {
      acts.push({
        label: 'Download Annotated Video',
        onClick: () => {
          if (!props.annotatedVideo) {
            return;
          }

          onClick([
            {
              name: StereoVideoType.Annotated,
              video: props.annotatedVideo,
            },
          ]);
        },
      });
    }

    if (props.replayVideo) {
      acts.push({
        label: 'Download Replay Video',
        onClick: () => {
          if (!props.replayVideo) {
            return;
          }

          onClick([
            {
              name: StereoVideoType.Replay,
              video: props.replayVideo,
            },
          ]);
        },
      });
    }

    if (props.rawVideo) {
      acts.push({
        label: 'Download Raw Video',
        onClick: () => {
          if (!props.rawVideo) {
            return;
          }

          onClick([
            {
              name: StereoVideoType.Raw,
              video: props.rawVideo,
            },
          ]);
        },
      });
    }

    if (props.spinsVideo) {
      acts.push({
        label: 'Download Spin Video',
        onClick: () => {
          if (!props.spinsVideo) {
            return;
          }

          onClick([
            {
              name: StereoVideoType.Spins,
              video: props.spinsVideo,
            },
          ]);
        },
      });
    }

    return acts;
  }, [props.replayVideo, props.rawVideo, props.spinsVideo]);

  // no videos
  if (!actions.length) {
    return null;
  }

  if (actions.length === 1) {
    const action = first(actions);
    return (
      <Box position="absolute" right="0">
        <DialogButton
          onClick={action?.onClick}
          label="Download Video"
          icon={<DownloadIcon />}
          variant="solid"
          loading={loading}
        />
      </Box>
    );
  }

  return (
    <CommonMenu
      title="Download Videos"
      side="bottom"
      trigger={
        <Button loading={loading}>
          Download Videos <ChevronDownIcon />
        </Button>
      }
      actions={actions}
      skipSort
    />
  );
};

export const MachineShotDetails = (props: IShotDetailsProps) => {
  const { shot, pitch, hasNext, hasPrev, onBack } = props;
  const { name: pitchName } = pitch;

  const [loading, setLoading] = useState(false);

  const [videoData, setVideoData] = useState<IAnnotationDetails>();

  const [videoType, setVideoType] = useState(getDefaultVideoType(shot));

  const [videos, setVideos] = useState<IStereoVideo[]>([]);

  const replayVideo = useMemo(
    () => videos.find((m) => m._id === shot.replay_video_id),
    [videos, shot.replay_video_id]
  );

  const rawVideo = useMemo(
    () => videos.find((m) => m._id === shot.raw_video_id),
    [videos, shot.raw_video_id]
  );

  const spinsVideo = useMemo(
    () => videos.find((m) => m._id === shot.spins_video_id),
    [videos, shot.spins_video_id]
  );

  const annotatedVideo = useMemo(
    () => videos.find((m) => m._id === shot.annotated_video_id),
    [videos, shot.annotated_video_id]
  );

  const [stereoVideosLoaded, setStereoVideosLoaded] = useState(false);

  const [summary, setSummary] = useState<IShotPrecursorSummary>();
  const [hit, setHit] = useState<ISessionHit>();

  const [activeTab, setActiveTab] = useState<ShotDetailsTabKey>(
    ShotDetailsTabKey.PitchData
  );

  // detection issue form
  const [dialogDetectionIssue, setDialogDetectionIssue] = useState<{
    key: number;
    hitID: string;
    detectionIssue?: Nullable<IDetectionIssue>;
  }>();

  const machine = useMachinesPortalStore((s) => s.machine);

  const hasVision =
    !!machine && VISION_ENABLED_HW_VERSIONS.includes(machine.hardware_version);

  // reload summary and videos whenever shot changes
  useEffect(() => {
    const getSummary = async () => {
      const result = await ShotsService.getInstance().getPrecursorSummary(
        shot._id
      );
      setSummary(result);
    };

    const getShotVideos = async () => {
      // ? empty values will be omitted
      const videoIDs = compact(
        uniq([
          shot.replay_video_id,
          shot.annotated_video_id,
          shot.raw_video_id,
          shot.spins_video_id,
        ])
      );

      if (videoIDs.length === 0) {
        setVideos([]);
        return;
      }

      const results = await StereoVideosService.getInstance().getMany(videoIDs);
      setVideos(results);
    };

    const init = async () => {
      setLoading(true);

      setVideoType(getDefaultVideoType(shot));

      await getSummary();

      if (shot.hit_id && shot.fire_id) {
        await HittersService.getInstance()
          .getHitByFire(shot.fire_id)
          .then((result) => setHit(result));
      } else {
        setHit(undefined);
      }

      const defaultVideoID =
        shot.replay_video_id ||
        shot.annotated_video_id ||
        shot.raw_video_id ||
        shot.spins_video_id;

      if (!defaultVideoID) {
        setVideoData(undefined);
      }

      await getShotVideos();

      setLoading(false);
      setStereoVideosLoaded(true);
    };

    init();
  }, [shot]);

  useEffect(() => {
    if (!shot) {
      return;
    }

    if (!stereoVideosLoaded) {
      return;
    }

    const callback = async (video: IStereoVideo | undefined) => {
      if (!video?.path) {
        setVideoData(undefined);
        return;
      }

      setVideoData({
        shotID: props.shot._id,
        videoID: video._id,
        hitterID: hit?.hitter?._id,
      });
    };

    switch (videoType) {
      case StereoVideoType.Annotated:
        callback(annotatedVideo);
        break;

      case StereoVideoType.Replay:
        callback(replayVideo);
        break;

      case StereoVideoType.Raw:
        callback(rawVideo);
        break;

      case StereoVideoType.Spins: {
        callback(spinsVideo);
        break;
      }

      default: {
        break;
      }
    }
  }, [
    shot,
    stereoVideosLoaded,
    hit?.hitter?._id,
    videoType,
    replayVideo,
    annotatedVideo,
    rawVideo,
    spinsVideo,
    setVideoData,
  ]);

  // e.g. if the user is looking at a shot with a Rapsodo shot then goes to the next shot which is missing it, we'll fall back to the Compare tab
  useEffect(() => {
    const rapsodoTabMismatch =
      activeTab === ShotDetailsTabKey.RapsodoShot && !shot.rapsodo_shot_id;
    const msbsTabMismatch =
      activeTab === ShotDetailsTabKey.MsbsShot && !shot.msbs_shot_id;
    const trackmanTabMismatch =
      activeTab === ShotDetailsTabKey.TrackmanShot && !shot.trackman_pitch_id;
    const hawkeyeTabMismatch =
      activeTab === ShotDetailsTabKey.HawkeyeShot && !shot.hawkeye_pitch_id;

    const tabMismatch =
      rapsodoTabMismatch ||
      msbsTabMismatch ||
      trackmanTabMismatch ||
      hawkeyeTabMismatch;

    if (tabMismatch) {
      setActiveTab(ShotDetailsTabKey.PitchData);
    }
  }, [
    activeTab,
    shot.rapsodo_shot_id,
    shot.msbs_shot_id,
    shot.trackman_pitch_id,
    shot.hawkeye_pitch_id,
  ]);

  // todo: move Pitch Data / Hit Data tables to their own components
  const pitchDataColumns = useMemo<ITypedTableColumn<IPitchDataRow>[]>(
    () => [
      {
        label: '',
        key: '_name',
        formatFn: (r) => (
          <Flex direction="column" py={RADIX.FLEX.PAD.SM}>
            <Text size={RADIX.TEXT.SIZE.SM}>{r.label}</Text>
            {r.subLabel && (
              <Text size={RADIX.TEXT.SIZE.XS} color={RADIX.COLOR.SECONDARY}>
                {r.subLabel}
              </Text>
            )}
          </Flex>
        ),
      },
      {
        label: 'common.speed',
        subLabel: 'mph',
        key: '_speed',
        formatFn: (r) => {
          return (
            <Flex gap={RADIX.FLEX.GAP.XS} px="0" py="3">
              <Text>
                {r.speedMPH !== undefined ? r.speedMPH.toFixed(1) : 'N/A'}
              </Text>
            </Flex>
          );
        },
      },
      {
        label: 'common.spin',
        key: '_spin',
        subLabel: 'rpm',
        formatFn: (r) => {
          return (
            <Flex gap={RADIX.FLEX.GAP.XS} px="0" py="3">
              <Text>
                {r.netSpin !== undefined ? r.netSpin.toFixed(0) : 'N/A'}
              </Text>
            </Flex>
          );
        },
      },
      {
        label: 'common.v-break',
        key: '_zInches',
        subLabel: 'in',
        formatFn: (r) => {
          return (
            <Flex gap={RADIX.FLEX.GAP.XS} px="0" py="3">
              <Text>
                {r.break?.zInches !== undefined
                  ? r.break.zInches.toFixed(1)
                  : 'N/A'}
              </Text>
            </Flex>
          );
        },
      },
      {
        label: 'common.h-break',
        key: '_xInches',
        subLabel: 'in',
        formatFn: (r) => {
          return (
            <Flex gap={RADIX.FLEX.GAP.XS} px="0" py="3">
              <Text>
                {r.break?.xInches !== undefined
                  ? (-1 * r.break.xInches).toFixed(1)
                  : 'N/A'}
              </Text>
            </Flex>
          );
        },
      },
      {
        label: 'common.plate-x',
        key: '_xFeet',
        subLabel: 'ft',
        formatFn: (r) => {
          return (
            <Flex gap={RADIX.FLEX.GAP.XS} px="0" py="3">
              <Text>
                {r.plate?.plate_x !== undefined
                  ? r.plate.plate_x.toFixed(1)
                  : 'N/A'}
              </Text>
            </Flex>
          );
        },
      },
      {
        label: 'common.plate-z',
        key: '_zFeet',
        subLabel: 'ft',
        formatFn: (r) => {
          return (
            <Flex gap={RADIX.FLEX.GAP.XS} px="0" py="3">
              <Text>
                {r.plate?.plate_z !== undefined
                  ? r.plate.plate_z.toFixed(1)
                  : 'N/A'}
              </Text>
            </Flex>
          );
        },
      },
    ],
    []
  );

  const hitDataColumns = useMemo<ITypedTableColumn<IHitDataRow>[]>(
    () => [
      {
        label: '',
        key: '_name',
        formatFn: (r) => (
          <Flex direction="column" py={RADIX.FLEX.PAD.SM}>
            <Text size={RADIX.TEXT.SIZE.SM}>{r.label}</Text>
            {r.subLabel && (
              <Text size={RADIX.TEXT.SIZE.XS} color={RADIX.COLOR.SECONDARY}>
                {r.subLabel}
              </Text>
            )}
          </Flex>
        ),
      },
      {
        label: 'EV',
        subLabel: 'mph',
        key: '_ev',
        formatFn: (r) => {
          return (
            <Flex gap={RADIX.FLEX.GAP.XS} px="0" py="3">
              <Text>{isNil(r.exitMPH) ? 'N/A' : r.exitMPH.toFixed(1)}</Text>
            </Flex>
          );
        },
      },
      {
        label: 'Dist.',
        subLabel: 'ft',
        key: '_dist',
        formatFn: (r) => {
          return (
            <Flex gap={RADIX.FLEX.GAP.XS} px="0" py="3">
              <Text>
                {isNil(r.distanceFT) ? 'N/A' : r.distanceFT.toFixed(0)}
              </Text>
            </Flex>
          );
        },
      },
      {
        label: 'LA',
        key: '_la',
        subLabel: 'deg',
        formatFn: (r) => {
          return (
            <Flex gap={RADIX.FLEX.GAP.XS} px="0" py="3">
              <Text>
                {isNil(r.vLaunchDEG) ? 'N/A' : r.vLaunchDEG.toFixed(1)}
              </Text>
            </Flex>
          );
        },
      },
      {
        label: 'Exit Dir.',
        key: '_exitDir',
        subLabel: 'deg',
        formatFn: (r) => {
          return (
            <Flex gap={RADIX.FLEX.GAP.XS} px="0" py="3">
              <Text>
                {isNil(r.hLaunchDEG) ? 'N/A' : r.hLaunchDEG.toFixed(1)}
              </Text>
            </Flex>
          );
        },
      },
      {
        label: 'Result',
        key: '_result',
        formatFn: (r) => {
          return (
            <Flex gap={RADIX.FLEX.GAP.XS} px="0" py="3">
              <Text>
                {HitHelper.getOutcomeLabel(
                  r._mx_outcome ?? OutcomeCode.Unknown
                ) ?? 'N/A'}
              </Text>
            </Flex>
          );
        },
      },
    ],
    []
  );

  const openDetectionIssueDialog = (hit: ISessionHit) => {
    setDialogDetectionIssue({
      key: Date.now(),
      hitID: hit._id,
      detectionIssue: hit.detectionIssue,
    });
  };

  const reportDetectionIssue = async (
    hitID: string,
    detectionIssue: Partial<IDetectionIssue>
  ) => {
    const updatedHit = await HittersService.getInstance().upsertDetectionIssue(
      hitID,
      detectionIssue
    );
    setHit(updatedHit);
  };

  const showNavButtons = hasPrev || hasNext;

  const msbsShot = summary?.msbsShot;
  const rapsodoShot = summary?.rapsodoShot;
  const trackmanPitch = summary?.trackmanPitch;
  const hawkeyePitch = summary?.hawkeyePitch;

  const numVideos = videos.length;

  // an msbs Traj indicates we have vision data, even if the tracking device isn't set to Vision
  const showDetectionIssueDialog = hit && msbsShot?.Traj;

  return (
    <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
      <Flex gap={RADIX.FLEX.GAP.SM} align="center">
        <CommonBackArrow onClick={onBack} />
        <CommonTooltip
          text="Click to copy this shot ID to the clipboard"
          trigger={
            <Heading
              className="cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                navigator.clipboard.writeText(shot._id);
                NotifyHelper.success({
                  message_md: `Shot ID ${shot._id} copied to clipboard!`,
                });
              }}
              size={RADIX.TEXT.SIZE.LG}
            >
              {pitchName}
            </Heading>
          }
        />
        <Badge color={RADIX.COLOR.NEUTRAL}>{formatDate(shot._created)}</Badge>
        <MachineShotConfidenceBadge shot={shot} pitch={pitch} withSuffix />
        <Flex gap={RADIX.FLEX.GAP.SM} ml="auto" align="center">
          {showDetectionIssueDialog && (
            <IconButton
              variant="soft"
              size="2"
              ml="auto"
              onClick={() => openDetectionIssueDialog(hit)}
            >
              <DetectionFlag filled={!!hit?.detectionIssue} />
            </IconButton>
          )}
          {showNavButtons && (
            <>
              <DialogButton
                icon={<ArrowLeftIcon />}
                label="Next"
                onClick={props.onNextShot}
                disabled={!hasNext || props.loadingPrev}
              />
              <DialogButton
                suffixIcon={<ArrowRightIcon />}
                label="Previous"
                onClick={props.onPrevShot}
                disabled={!hasPrev || props.loadingPrev}
                loading={props.loadingPrev}
              />
            </>
          )}
        </Flex>
      </Flex>
      <Box mb="2" position="relative">
        {videoData && (
          <AnnotatedVideoPlayer
            details={videoData}
            extraControls={
              numVideos > 0 && (
                <SegmentedControl.Root
                  value={videoType}
                  style={{ background: 'var(--gray-3)' }}
                  onValueChange={(v) => setVideoType(v as StereoVideoType)}
                  size="1"
                >
                  {annotatedVideo && (
                    <SegmentedControl.Item
                      value={StereoVideoType.Annotated}
                      className="cursor-pointer"
                    >
                      Annotated
                    </SegmentedControl.Item>
                  )}
                  {replayVideo && (
                    <SegmentedControl.Item
                      value={StereoVideoType.Replay}
                      className="cursor-pointer"
                    >
                      Replay
                    </SegmentedControl.Item>
                  )}
                  {rawVideo && (
                    <SegmentedControl.Item
                      value={StereoVideoType.Raw}
                      className="cursor-pointer"
                    >
                      Raw
                    </SegmentedControl.Item>
                  )}
                  {spinsVideo && (
                    <SegmentedControl.Item
                      value={StereoVideoType.Spins}
                      className="cursor-pointer"
                    >
                      Spin
                    </SegmentedControl.Item>
                  )}
                </SegmentedControl.Root>
              )
            }
            autoplay
          />
        )}
      </Box>
      <Flex direction="column" position="relative">
        <CommonTabs
          tabs={[
            {
              label: ShotDetailsTabKey.PitchData,
              value: ShotDetailsTabKey.PitchData,
              content: (
                <Box
                  style={{
                    position: 'relative',
                  }}
                >
                  <CommonTableHoC
                    dataTestID="ShotDetailsPitches"
                    identifier="ShotDetailsPitchesTable"
                    displayColumns={pitchDataColumns}
                    displayData={getPitchDataRows(
                      shot,
                      hasVision,
                      summary?.precursors ?? []
                    )}
                  />
                </Box>
              ),
            },
            {
              label: ShotDetailsTabKey.HitData,
              value: ShotDetailsTabKey.HitData,
              // invisible: !sessionHit,
              content: (
                <Box
                  style={{
                    position: 'relative',
                  }}
                >
                  <CommonTableHoC
                    dataTestID="ShotDetailsHits"
                    identifier="ShotDetailsHitsTable"
                    displayColumns={hitDataColumns}
                    displayData={getHitDataRows(
                      shot,
                      hasVision,
                      summary?.precursors ?? [],
                      hit
                    )}
                  />
                </Box>
              ),
            },
            {
              label: ShotDetailsTabKey.MachineShot,
              value: ShotDetailsTabKey.MachineShot,
              content: (
                <Box position="relative">
                  <Card>
                    <JsonViewer value={shot} />
                  </Card>
                  {shot && (
                    <FloatingCopyButton
                      value={JSON.stringify(shot, null, 2)}
                      label="Machine Shot"
                    />
                  )}
                </Box>
              ),
            },
            {
              label: ShotDetailsTabKey.RapsodoShot,
              value: ShotDetailsTabKey.RapsodoShot,
              invisible: !shot.rapsodo_shot_id,
              content: (
                <Skeleton loading={loading}>
                  <Box position="relative">
                    <Card>
                      <JsonViewer value={rapsodoShot} />
                    </Card>
                    {rapsodoShot && (
                      <FloatingCopyButton
                        value={JSON.stringify(rapsodoShot, null, 2)}
                        label="Rapsodo Shot"
                      />
                    )}
                  </Box>
                </Skeleton>
              ),
            },
            {
              label: ShotDetailsTabKey.TrackmanShot,
              value: ShotDetailsTabKey.TrackmanShot,
              invisible: !shot.trackman_pitch_id,
              content: (
                <Skeleton loading={loading}>
                  <Box position="relative">
                    <Card>
                      <JsonViewer value={trackmanPitch} />
                    </Card>
                    {trackmanPitch && (
                      <FloatingCopyButton
                        value={JSON.stringify(trackmanPitch, null, 2)}
                        label="Trackman Shot"
                      />
                    )}
                  </Box>
                </Skeleton>
              ),
            },
            {
              label: ShotDetailsTabKey.MsbsShot,
              value: ShotDetailsTabKey.MsbsShot,
              invisible: !shot.msbs_shot_id,
              content: (
                <Box position="relative">
                  <Card>
                    <JsonViewer value={msbsShot} />
                  </Card>
                  {msbsShot && (
                    <FloatingCopyButton
                      value={JSON.stringify(msbsShot, null, 2)}
                      label="MSBS Shot"
                    />
                  )}
                </Box>
              ),
            },
            {
              label: ShotDetailsTabKey.HawkeyeShot,
              value: ShotDetailsTabKey.HawkeyeShot,
              invisible: !shot.hawkeye_pitch_id,
              content: (
                <Skeleton loading={loading}>
                  <Box position="relative">
                    <Card>
                      <JsonViewer value={hawkeyePitch} />
                    </Card>
                    {hawkeyePitch && (
                      <FloatingCopyButton
                        value={JSON.stringify(hawkeyePitch, null, 2)}
                        label="Hawkeye Shot"
                      />
                    )}
                  </Box>
                </Skeleton>
              ),
            },
          ]}
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as ShotDetailsTabKey)}
          gap="1"
        />
        <Box
          position="absolute"
          right="0"
          display={stereoVideosLoaded ? 'block' : 'none'}
        >
          <DownloadVideosButton
            shotID={shot._id}
            replayVideo={replayVideo}
            rawVideo={rawVideo}
            annotatedVideo={annotatedVideo}
            spinsVideo={spinsVideo}
            ready={stereoVideosLoaded}
          />
        </Box>
        {dialogDetectionIssue && (
          <DetectionIssueDialog
            key={dialogDetectionIssue.key}
            hitID={dialogDetectionIssue.hitID}
            defaultDetectionIssue={dialogDetectionIssue.detectionIssue}
            onSave={reportDetectionIssue}
            onClose={() => setDialogDetectionIssue(undefined)}
          />
        )}
        <BlurOverlay show={loading} />
      </Flex>
    </Flex>
  );
};
